import { useState, useEffect, useRef } from "react"

// Icons
import { ChevronLeft, Trash2, X, Hotel, Building2 } from "lucide-react"

// UI components
import Badge from "../../../components/ui/badge/badge"
import HotelPicker from "./hotel-picker"

// ---------------------------------------------------------------------------
// The flow builder - shown in the left panel when a flow is open. Two steps:
// pick the hotel the client enquired for, then stack up the alternatives we'd
// quote alongside it. Every choice writes straight to the flow document, and
// the diagram on the right redraws from the same data in realtime.
// ---------------------------------------------------------------------------
export default function FlowBuilder({ flow, onBack, onSave, onDelete, onSketch }) {
	// Two-step delete: first click arms the button, second click deletes.
	// Disarms itself after a few seconds if left alone.
	const [confirmingDelete, setConfirmingDelete] = useState(false)
	const disarmTimer = useRef(null)

	useEffect(() => () => clearTimeout(disarmTimer.current), [])

	const handleDelete = () => {
		if (!confirmingDelete) {
			setConfirmingDelete(true)
			disarmTimer.current = setTimeout(() => setConfirmingDelete(false), 3500)
			return
		}
		clearTimeout(disarmTimer.current)
		onDelete()
	}

	const alternatives = flow.alternatives || []

	// ---- mutations (all merge into the flow doc) ---------------------------
	const setStart = (hit) => {
		onSketch(null)
		onSave({ start: { id: hit.objectID, title: hit.title || "Hotel" } })
	}

	const clearStart = () => onSave({ start: null })

	const addAlternative = (hit) => {
		if (alternatives.some((alternative) => alternative.id === hit.objectID)) return
		onSketch(null)
		onSave({ alternatives: [...alternatives, { id: hit.objectID, title: hit.title || "Hotel" }] })
	}

	const removeAlternative = (alternativeId) => {
		onSave({ alternatives: alternatives.filter((alternative) => alternative.id !== alternativeId) })
	}

	// Everything already in the flow is excluded from both pickers
	const chosenIds = [...(flow.start ? [flow.start.id] : []), ...alternatives.map((alternative) => alternative.id)]

	return (
		<div className="qa-builder">
			{/* Top bar - back to the list + delete this flow */}
			<div className="qa-builder-bar">
				<button className="qa-back" onClick={onBack}>
					<ChevronLeft size={15} />
					All flows
				</button>

				<button className={["qa-delete", confirmingDelete && "is-armed"].join(" ")} onClick={handleDelete}>
					<Trash2 size={13} />
					{confirmingDelete ? "Click again to confirm" : "Delete flow"}
				</button>
			</div>

			{/* ---------------------------------------------------------- */}
			{/* STEP 1 - the hotel the client enquired for                  */}
			{/* ---------------------------------------------------------- */}
			<div className="qa-step">
				<div className="qa-step-heading">
					<span className="qa-step-number">1</span>
					<h3>Starting hotel</h3>
				</div>
				<p className="qa-step-hint">If a client enquires for this hotel...</p>

				{flow.start ? (
					<div className="qa-chosen is-start">
						<div className="qa-chosen-icon">
							<Hotel size={15} />
						</div>
						<span className="qa-chosen-title">{flow.start.title}</span>
						<Badge label="Start" type="NEGATIVE" />
						<span className="qa-chosen-remove" onClick={clearStart} title="Remove">
							<X size={14} />
						</span>
					</div>
				) : (
					<HotelPicker
						placeholder="Search for a hotel..."
						excludeIds={chosenIds}
						autoFocus={true}
						onPick={setStart}
						onType={(text) => onSketch(text ? { target: "start", text } : null)}
					/>
				)}
			</div>

			{/* ---------------------------------------------------------- */}
			{/* STEP 2 - the alternatives quoted alongside it               */}
			{/* ---------------------------------------------------------- */}
			<div className={["qa-step", !flow.start && "is-waiting"].join(" ")}>
				<div className="qa-step-heading">
					<span className="qa-step-number">2</span>
					<h3>Also quote</h3>
					{alternatives.length > 0 && <span className="qa-step-count">{alternatives.length}</span>}
				</div>
				<p className="qa-step-hint">...then offer these hotels in the same quote.</p>

				{flow.start ? (
					<>
						<HotelPicker
							placeholder="Search for alternatives..."
							excludeIds={chosenIds}
							onPick={addAlternative}
							onType={(text) => onSketch(text ? { target: "alternative", text } : null)}
						/>

						{alternatives.length > 0 ? (
							<div className="qa-chosen-list">
								{alternatives.map((alternative, index) => (
									<div key={alternative.id} className="qa-chosen" style={{ animationDelay: `${index * 30}ms` }}>
										<div className="qa-chosen-icon is-alternative">
											<Building2 size={15} />
										</div>
										<span className="qa-chosen-title">{alternative.title}</span>
										<span className="qa-chosen-remove" onClick={() => removeAlternative(alternative.id)} title="Remove">
											<X size={14} />
										</span>
									</div>
								))}
							</div>
						) : (
							<p className="qa-step-empty">No alternatives added yet.</p>
						)}
					</>
				) : (
					<p className="qa-step-empty">Choose a starting hotel first.</p>
				)}
			</div>
		</div>
	)
}
